// @ts-check
// powersync-bootstrap.js — Wires the PowerSync (Desktop + iOS) reactive sync
// into the running app: forwards `powersync:change` events to a UI refresh,
// installs the status-pill tap-to-sync handler, and on Capacitor sets up the
// extra always-visible refresh button.
//
// IMPORTANT: powersync-storage.js is a type="module" script whose async IIFE
// sets window.PowerSyncNoteStorage *after* several awaits.  By the time the
// synchronous body of this deferred script runs, that value may not yet be
// set — we therefore wrap the setup in a function and call it either
// immediately (Electron fast path) or via the powersync:ready event (iOS).
//
// References mutable app state through bare-name globals declared in
// app-state.js (getCurrentFileName(), getLastSavedContent(), …).

import {
  _isSecondary,
  getCurrentFileName,
  getLastRemoteContent,
  getLastSavedContent,
  getLoadNoteGeneration,
  getReadOnlyViewActive,
  previewDiv,
  setCurrentFileName,
  setLastSavedContent,
} from '../core/app-state.js';
import { NoteEditor } from '../editor/note-editor.js';
import { isEncryptionReadOnly } from '../encryption/state.js';
import { applyInboundConflict } from '../notes/conflict-apply.js';
import { applyBackgroundConflict } from '../notes/conflict-apply-core.js';
import { isDenyListed } from '../notes/conflict-detect.js';
import { updateFileList } from '../notes/file-list.js';
import { invalidateScheduleCache } from '../schedule/schedule.js';
import { isLocalEcho } from '../storage/note-local-echo.js';
import { NoteStorage as _IdbNoteStorage } from '../storage/storage.js';
import { updateStatus } from '../ui/status-pill.js';
import { applySyncedPreferences } from '../ui/theme-engine.js';

// Resolve the active NoteStorage at call time.  On Desktop/iOS
// window.NoteStorage is the PowerSync (SQLite) backend; the static
// module import is the IndexedDB fallback.  handlePowerSyncChange()
// must read from the live backend so structural-change detection and
// content comparisons work against real data.
/** @returns {any} the active NoteStorage backend. */
function _getNoteStorage() {
  return window.NoteStorage || _IdbNoteStorage;
}

(() => {
  // ── PowerSync reactive sync (Desktop + iOS) ──────────────────────────────
  // PowerSync handles all sync automatically. We just listen for changes
  // and refresh the UI when the local SQLite database is updated.
  //
  // IMPORTANT: powersync-storage.js is a type="module" script whose async IIFE
  // sets window.PowerSyncNoteStorage *after* several awaits (db.init, db.connect).
  // By the time the synchronous body of this deferred script runs, that value is
  // not yet set.  We therefore wrap the setup in a function and call it either
  // immediately (when already ready, e.g. Electron fast path) or via the
  // powersync:ready event (iOS, where the async init finishes later).

  /** @type {any} */
  let _syncKnownNames: any = null;

  // FORK-ONLY background-merge state. `_lastPlaintextSnapshot` is this device's
  // plaintext name→content from the END of the previous sweep — the pre-LWW
  // "local" side for every note (refreshCache overwrites the live caches with the
  // absorbed remote, so the local side is otherwise lost). It is carried across
  // sweeps in module scope; a workspace switch reloads the page (the documented
  // switch===reload invariant), which resets it — so there is no cross-workspace
  // localPre bleed. (If in-place switching is ever added, reset this on switch.)
  // INVARIANT: this is a FULL carry-forward of every note's plaintext — it must
  // NEVER be pruned to the change-feed's changed-set. The change-feed below
  // narrows which notes we DECRYPT+examine each tick; it does not narrow the
  // localPre source. Pruning would drop the pre-sync local side of a note that
  // was unchanged for several ticks and then changed, so its fork would see
  // localPre==null and silently accept remote — the exact loss the sweep exists
  // to prevent.
  /** @type {Map<string,string> | null} */
  let _lastPlaintextSnapshot: Map<string, string> | null = null;
  // Change-feed cursor: the max cleartext `updated_at` seen at the end of the
  // last CLEAN sweep. Captured-at-start, advanced-on-clean-completion (a thrown
  // or bailed sweep leaves it, so the next sweep re-covers the same window).
  // Module-scope like the snapshot, so the switch===reload invariant resets it.
  /** @type {string | null} */
  let _lastSeenUpdatedAt: string | null = null;
  // Coalescing re-entrancy guard: a sweep takes longer now (the per-note pass),
  // so a `powersync:change` arriving mid-sweep must not re-enter concurrently —
  // it sets a rerun flag and we run exactly once more after, never in parallel.
  let _sweepRunning = false;
  let _sweepQueued = false;

  /** Coalescing wrapper around the sweep — never runs two sweeps concurrently. */
  /** @returns {Promise<void>} */
  async function handlePowerSyncChange() {
    // Secondary windows (Ctrl+Shift+N) proxy every NoteStorage call to the
    // primary, which runs the authoritative conflict sweep. Running the sweep
    // here too is not just redundant — it's unsafe: the proxy has no
    // getMergeBase, so the base-less sweep would mis-read every synced change as
    // a conflict and mint spurious "conflicted copy" notes (via proxied setNote,
    // echoed back to every window). Just refresh this window's list UI from the
    // primary's already-reconciled data and stop.
    if (_isSecondary) {
      invalidateScheduleCache();
      await updateFileList();
      return;
    }
    if (_sweepRunning) {
      _sweepQueued = true;
      return;
    }
    _sweepRunning = true;
    try {
      await _runPowerSyncChange();
    } catch (e) {
      console.error('[powersync] sync sweep failed:', e);
    } finally {
      _sweepRunning = false;
      if (_sweepQueued) {
        _sweepQueued = false;
        queueMicrotask(handlePowerSyncChange);
      }
    }
  }

  /** @returns {Promise<void>} */
  async function _runPowerSyncChange() {
    if (!window.PowerSyncNoteStorage) return;

    const NS = _getNoteStorage();

    // ── Encryption guard: if this device needs a key but doesn't have one,
    // detect newly-arrived encrypted content and warn the user.
    // Do NOT process content changes — we risk corrupting data by saving
    // decrypted-as-ciphertext or overwriting remote encrypted notes.
    if (isEncryptionReadOnly()) {
      // Refresh cache to pick up latest note names
      if (typeof NS.refreshCache === 'function') {
        await NS.refreshCache();
      }
      // Check if encrypted content has arrived
      try {
        const names = await NS.getAllNoteNames();
        for (const name of names.slice(0, 5)) {
          // sample a few
          const raw = await (window.NoteStorage._unwrapped || window.NoteStorage).getNote(name);
          if (raw && typeof raw === 'string' && raw.startsWith('enc:v1:')) {
            updateStatus('Encrypted notes detected. Open Settings to pair this device.', false);
            // Mark editor as read-only to prevent edits being saved as plaintext
            if (!NoteEditor.isReadOnly()) {
              NoteEditor.setReadOnly(true);
            }
            await updateFileList();
            return; // Do not process content changes
          }
        }
      } catch {
        /* continue normally if check fails */
      }
    }

    // Capture the current loadNote generation so we can detect if the user
    // navigates to a different note while this handler is running.  If they
    // do, we bail out early to avoid overwriting the newly-loaded note.
    const gen = getLoadNoteGeneration();

    let structuralChanged = false;
    let contentChanged = false;

    // Refresh the in-memory cache first so that applySyncedPreferences()
    // reads the latest .app_preferences written by the remote device rather
    // than stale cached data.
    if (typeof NS.refreshCache === 'function') {
      await NS.refreshCache();
    }

    if (gen !== getLoadNoteGeneration()) return;

    // Re-apply synced preferences now that the cache is fresh — the
    // sync-settle device PULL (a remote theme/calendar-colour change lands here).
    await applySyncedPreferences();

    // Abort if the user navigated away during the async preference apply.
    if (gen !== getLoadNoteGeneration()) return;

    // Skip the editable-note reconciliation for a virtual read-only generated
    // view (Note Graph, Labyrinth): its "current file" isn't a stored note, so
    // getNote() returns null and the deleted-remotely branch below would wrongly
    // tear the view down on every sync change. The generated view refreshes its
    // own data (e.g. the Labyrinth's rebuildFromStorage) independently.
    if (getCurrentFileName() && !getReadOnlyViewActive()) {
      const liveContent = NoteEditor.getContent();
      const hasUnsavedEdits =
        getLastSavedContent() !== null && liveContent !== getLastSavedContent();
      const content = await NS.getNote(getCurrentFileName());

      // Abort if navigated away while we were fetching the note content.
      if (gen !== getLoadNoteGeneration()) return;

      if (content === null) {
        // Only restore the note if the user has actually made local edits that
        // haven't been saved yet. Checking value.trim() was wrong: it caused any
        // open note (even one just being read) to be resurrected whenever it was
        // deleted on another device, because the editor still held its content.
        if (hasUnsavedEdits) {
          try {
            await NS.setNote(getCurrentFileName(), liveContent);
            setLastSavedContent(liveContent);
            updateStatus('Note restored after sync conflict.', true);
          } catch {
            updateStatus('Note may have been deleted on another device.', false);
          }
        } else if (getLastRemoteContent() === null) {
          // loadNote is still in flight: it sets the current filename BEFORE
          // it awaits getMergeBase + commits the remote baseline (so
          // ImageWidget._resolveImageSrc sees the right note when the editor
          // rebuilds widgets — image-attachments fix 1e3668a). A sweep that
          // races into that window can read getNote(name) === null simply
          // because PowerSync's local SQLite hasn't surfaced the row yet,
          // NOT because the note was deleted on another device. Skip the
          // clear; a later sweep that runs after loadNote settles will catch
          // a genuine remote delete (getLastRemoteContent is non-null by then).
        } else {
          setCurrentFileName(null);
          localStorage.removeItem('current_file');
          if (getReadOnlyViewActive()) previewDiv.innerHTML = '';
          updateStatus('Current note was deleted on another device.', false);
        }
      } else if (content !== liveContent && content !== getLastSavedContent()) {
        // Route the inbound update through the shared conflict-detect floor. This
        // replaces the old no-op "keeping your unsaved edits" toast (which
        // silently dropped the remote) with a never-lose classifier: ignore echo,
        // fast-forward / rebase the safe cases, or fork a conflicted copy on true
        // divergence. The recheckGen guard preserves the existing gen-protection
        // before any editor write or sidecar dispatch.
        const outcome = await applyInboundConflict({
          name: getCurrentFileName() as any,
          base: getLastRemoteContent(),
          local: liveContent,
          remote: content,
          saved: getLastSavedContent(),
          hasUnsavedEdits,
          NS,
          recheckGen: () => gen !== getLoadNoteGeneration(),
        });
        if (outcome.contentChanged) contentChanged = true;
        if (outcome.structuralChanged) structuralChanged = true;
      }
    }

    if (gen !== getLoadNoteGeneration()) return;

    // ── Background sweep (FORK-ONLY): resolve every OTHER changed note ─────────
    // The open note was handled above; every other note that changed in this
    // batch was silently absorbed by PowerSync's LWW. Pull only the notes whose
    // cleartext `updated_at` advanced since the last clean sweep (getChangedSince
    // — decrypts ONLY that subset, not the whole vault), and diff each against
    // this device's carried pre-sync plaintext: a note whose local edit was
    // overwritten by a divergent remote gets a deterministic conflicted-copy
    // sidecar holding the LOCAL edit (never lost); clean forwards and never-synced
    // notes just adopt remote. We never 3-way merge a note the user isn't
    // watching. The FIRST sweep (no prev) only seeds the snapshot (no forks),
    // which also collapses first-sync to zero forks. All reads/writes go through
    // window.NoteStorage (the encrypting + namespaced wrap), never the inner
    // handle — except the decrypt-free structural name scan below.
    const NSW = window.NoteStorage;
    const prevSnapshot = _lastPlaintextSnapshot;
    const cursorAtStart = _lastSeenUpdatedAt;
    // Changed-set, freshest post-LWW content (getChangedSince is a direct DB read
    // so it never returns stale cache). Fall back to a full getAllNotes diff on a
    // backend without the change-feed (the cursor then never advances → full
    // sweep each tick, the pre-change-feed behaviour).
    /** @type {Array<{name:string, content:string, updated_at:string|null}>} */
    let changed: Array<{ name: string; content: string; updated_at: string | null }> = [];
    if (NSW && typeof NSW.getChangedSince === 'function') {
      changed = await NSW.getChangedSince(cursorAtStart);
    } else if (NSW && typeof NSW.getAllNotes === 'function') {
      changed = (await NSW.getAllNotes()).map((r: any) => ({
        name: r.name,
        content: r.content,
        updated_at: null,
      }));
    }

    if (gen !== getLoadNoteGeneration()) return;

    // FULL carry-forward: prior plaintext for EVERY note, with the changed rows
    // updated. NEVER pruned to the changed-set (see the invariant at the decl) —
    // a note unchanged-for-N-ticks-then-changed keeps its true localPre here.
    const nextSnapshot = new Map(prevSnapshot || []);
    let maxSeen = cursorAtStart;
    for (const r of changed) {
      nextSnapshot.set(r.name, r.content);
      if (r.updated_at != null && (maxSeen == null || r.updated_at > maxSeen)) {
        maxSeen = r.updated_at;
      }
    }

    let forkCount = 0;
    if (prevSnapshot) {
      const openNote = getCurrentFileName();
      for (const { name: noteName, content: remote } of changed) {
        if (noteName === openNote) continue; // handled by the open-note path above
        if (noteName.startsWith('.')) continue; // hidden / app-managed (prefs, calendar)
        if (isDenyListed(noteName)) continue; // calendar-managed → leave LWW
        if (noteName.includes(' (conflicted copy ')) continue; // never fork an existing fork
        // A note whose current content is one of THIS device's own recent writes is not a remote
        // conflict: the change-watch can't tell our writes from a download, and the sync can echo
        // any intermediate version back out of order. Recognise + skip it (updating localPre) so
        // a multi-write consolidation never forks itself; a genuine remote change still flows on.
        if (isLocalEcho(noteName, remote)) {
          prevSnapshot.set(noteName, remote);
          continue;
        }
        const localPre = prevSnapshot.has(noteName) ? prevSnapshot.get(noteName) : null;
        if (localPre === remote) continue; // unchanged for this device
        const base =
          NSW && typeof NSW.getMergeBase === 'function' ? await NSW.getMergeBase(noteName) : null;
        const outcome = await applyBackgroundConflict({
          name: noteName,
          localPre,
          remote,
          base,
          NS: NSW,
        });
        if (outcome.action === 'fork' && outcome.wroteFork) {
          // localPre is non-null here by construction — applyBackgroundConflict
          // only forks when localPre != null (and !== remote).
          nextSnapshot.set(outcome.forkName, localPre as string); // the sidecar we just wrote
          forkCount++;
        }
      }
      if (forkCount > 0) {
        structuralChanged = true; // new conflicted-copy notes appeared
        updateStatus(
          `${forkCount} conflicted ${forkCount === 1 ? 'copy' : 'copies'} created from sync.`,
          false
        );
      }
    }
    _lastPlaintextSnapshot = nextSnapshot;
    // Advance the cursor ONLY on clean completion (here). An early `return` above
    // on a generation change leaves it, so the next sweep re-covers this window —
    // safe because applyBackgroundConflict is idempotent (re-running a row is a
    // skip/no-op; a re-fired fork write sees localPre===remote → skip).
    _lastSeenUpdatedAt = maxSeen;

    // Structural name-diff — decrypt-free, from the inner hashed-name set (opaque
    // tokens compared only for set-equality, never displayed; this still catches
    // remote additions/deletions, which a content-only change-feed can't see).
    const PSNS = window.PowerSyncNoteStorage;
    if (PSNS && typeof PSNS.getAllNoteNames === 'function') {
      const nameStr = (await PSNS.getAllNoteNames()).slice().sort().join('\n');
      if (_syncKnownNames !== null && _syncKnownNames !== nameStr) {
        structuralChanged = true;
      }
      _syncKnownNames = nameStr;
    }
    if (structuralChanged) {
      invalidateScheduleCache();
      await updateFileList();
    } else if (contentChanged) {
      invalidateScheduleCache();
    }
  }

  /** @returns {void} */
  function _setupPowerSyncHandlers() {
    if (!window.PowerSyncNoteStorage) return;

    window.addEventListener('powersync:change', handlePowerSyncChange);

    // On iOS, also refresh on app resume. Capacitor 7 exposes app lifecycle
    // via Capacitor.Plugins.App.addListener('appStateChange', …); the legacy
    // document 'resume' event (Cordova-era) never fires under Capacitor.
    if (window.Platform?.isCapacitor) {
      const App = window.Capacitor?.Plugins?.App;
      if (App && typeof App.addListener === 'function') {
        App.addListener('appStateChange', ({ isActive }: any) => {
          if (isActive) handlePowerSyncChange();
        });
      }
    }
  }

  // Call immediately if PowerSync is already ready (e.g. Electron where the
  // module finishes synchronously), otherwise wait for the ready event.
  // Both Electron and Capacitor (iOS/Android) now use the same PowerSync flow.
  if (window.PowerSyncNoteStorage) {
    _setupPowerSyncHandlers();
  } else if (window.Platform?.supportsSync) {
    window.addEventListener('powersync:ready', _setupPowerSyncHandlers, { once: true });
  }
})();
